import React from "react";
import { API, Auth } from 'aws-amplify';
import AvatarImage from "../avatar-image";
import {
    EmailIcon,
    EmailShareButton,
    FacebookIcon,
    FacebookShareButton,
    PinterestIcon,
    PinterestShareButton, RedditIcon, RedditShareButton,
} from "react-share";
import LoadingIndicator from "../loading-indicator";
import DateService from '../../services/date';
import * as mutations from '../../graphql/mutations';
import PostService from '../../services/post';

const openSwapLogo = require('../../images/open-swap-ico.svg');

const deleteIcon = require('../../images/fa-trash-o.svg');
const lockIcon = require('../../images/lock-pale.svg');

class PostCard extends React.Component {
    /*
    props:
    - isEditing
    - isDeleting
    - id
    - username
    - image
    - price
    - isPaid
    - currency
    - owner
    - caption
    - likes
    - comments
    - date
    - onDelete
    - onClone
    */

    constructor(props) {
        super(props);

        this.dateService = new DateService();

        const currentUserId = props.currentUser?.userId;
        const existingLike = props.myLike
            || (currentUserId && props.likes
                ? props.likes.find(l => l.author?._id === currentUserId || l._id === currentUserId)
                : null);

        this.state = {
            isShowingShare: false,
            isShowingComments: false,
            commentText: '',
            localComments: [],
            isLiked: !!existingLike,
            likeCount: props.likeCount ?? (props.likes ? props.likes.length : 0),
            likeId: existingLike?._id || null,
            isLiking: false,
            isSubmittingComment: false,
        };

        this.showShareSheet = this.showShareSheet.bind(this);
        this.hideShareSheet = this.hideShareSheet.bind(this);
        this.toggleShareSheet = this.toggleShareSheet.bind(this);
        this.toggleComments = this.toggleComments.bind(this);
        this.handleLike = this.handleLike.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.goToUser = this.goToUser.bind(this);
        this.handlePostCardCommentChange = this.handlePostCardCommentChange.bind(this);
        this.handleCommentSubmit = this.handleCommentSubmit.bind(this);
        this.handleCommentKeyDown = this.handleCommentKeyDown.bind(this);
    }

    goToUser(event) {
        event.preventDefault();
        this.props.navigation.userPage(event.currentTarget.name);
    }

    isPaid() {
        return this.props.isPaid;
    }

    findLikeId(likes) {
        const currentUserId = this.props.currentUser?.userId;
        if (!currentUserId || !likes) return null;
        const like = likes.find(l => l.author?._id === currentUserId || l._id === currentUserId);
        return like?._id || null;
    }

    async resolveLikeId() {
        if (this.state.likeId) return this.state.likeId;
        try {
            const response = await PostService.fetchPostById(this.props.postId);
            const post = response?.data?.posts?.[0] || response?.data?.[0];
            return this.findLikeId(post?.likes);
        } catch (e) {
            return null;
        }
    }

    async handleLike() {
        if (!this.props.currentUser || this.state.isLiking) return;

        const { isLiked, likeCount } = this.state;
        this.setState({ isLiked: !isLiked, likeCount: isLiked ? likeCount - 1 : likeCount + 1, isLiking: true });

        try {
            const authHeader = { Authorization: (await Auth.currentSession()).getIdToken().getJwtToken() };
            if (isLiked) {
                const likeId = await this.resolveLikeId();
                if (likeId) {
                    await API.graphql({ query: mutations.unlikeSocialPost, variables: { likeId } }, authHeader);
                    this.setState({ likeId: null, isLiking: false });
                } else {
                    // likeId not resolvable — revert optimistic update
                    this.setState({ isLiked, likeCount, isLiking: false });
                }
            } else {
                const result = await API.graphql({ query: mutations.likeSocialPost, variables: { postId: this.props.postId } }, authHeader);
                const updatedLikeCount = result?.data?.likeSocialPost?.likeCount;
                // Fetch the post to get the new likeId
                const fetchResponse = await PostService.fetchPostById(this.props.postId);
                const post = fetchResponse?.data?.posts?.[0] || fetchResponse?.data?.[0];
                const newLikeId = this.findLikeId(post?.likes);
                this.setState({
                    isLiking: false,
                    likeId: newLikeId,
                    ...(updatedLikeCount !== undefined && { likeCount: updatedLikeCount }),
                });
            }
        } catch (e) {
            console.error('Like error:', e);
            this.setState({ isLiked, likeCount, isLiking: false });
        }
    }

    toggleComments() {
        this.setState(prev => ({ isShowingComments: !prev.isShowingComments }));
    }

    handlePostCardCommentChange(event) {
        this.setState({ commentText: event.target.value });
    }

    handleCommentKeyDown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.handleCommentSubmit();
        }
    }

    async handleCommentSubmit() {
        const text = this.state.commentText.trim();
        if (!text || this.state.isSubmittingComment) return;

        this.setState({ isSubmittingComment: true, commentText: '' });
        try {
            const response = await PostService.saveComment(this.props.postId, text);
            const comment = response?.data?.comment;
            const username = comment?.author?.publicName
                || this.props.currentUser?.attributes?.nickname
                || this.props.currentUser?.username
                || 'You';
            this.setState(prev => ({
                localComments: [...prev.localComments, { username, text: comment?.text ?? text }],
                isSubmittingComment: false,
            }));
        } catch (e) {
            console.error('Comment error:', e);
            this.setState({ isSubmittingComment: false, commentText: text });
        }
    }

    handleDelete() {
        this.props.onDelete(this.props.postId);
    }

    showShareSheet() {
        this.setState({isShowingShare: true});
    }

    hideShareSheet() {
        this.setState({isShowingShare: false});
    }

    toggleShareSheet() {
        if (this.state.isShowingShare) {
            this.hideShareSheet();
        } else {
            this.showShareSheet();
        }
    }

    likesCount() {
        return this.props.likes ? this.props.likes.length : 0;
    }

    renderPostURL() {
        return `https://www.openswap.io/posts/${this.props.username}`;
    }

    renderShare() {
        if (!this.state.isShowingShare) return null;
        return (
            <div className="share-popup-backdrop" onClick={this.hideShareSheet}>
                <div className="share-popup" onClick={e => e.stopPropagation()}>
                    <div className="share-popup-header">
                        <span className="share-popup-title">Share</span>
                        <button className="share-popup-close" onClick={this.hideShareSheet}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="share-popup-icons">
                        <div className='share-item'>
                            <EmailShareButton subject={`Hey! Check out this great content I just found!`} url={this.renderPostURL()}>
                                <EmailIcon size={44} round={true}/>
                            </EmailShareButton>
                        </div>
                        <div className='share-item'>
                            <FacebookShareButton quote={this.props.username} url={this.renderPostURL()}>
                                <FacebookIcon size={44} round={true}/>
                            </FacebookShareButton>
                        </div>
                        <div className='share-item'>
                            <PinterestShareButton media={openSwapLogo} url={this.renderPostURL()}>
                                <PinterestIcon size={44} round={true}/>
                            </PinterestShareButton>
                        </div>
                        <div className='share-item'>
                            <RedditShareButton title={this.props.username} url={this.renderPostURL()}>
                                <RedditIcon size={44} round={true}/>
                            </RedditShareButton>
                        </div>
                        <div className='share-item'>
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(this.props.username)}&url=${encodeURIComponent(this.renderPostURL())}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="x-share-button"
                                aria-label="Share on X"
                            >
                                <svg viewBox="0 0 44 44" width="44" height="44" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="22" cy="22" r="22" fill="#000"/>
                                    <path d="M24.35 20.56 31.44 12h-1.7l-6.16 7.17L18.44 12H12l7.43 10.82L12 32h1.7l6.49-7.55L25.56 32H32l-7.65-11.44zm-2.3 2.67-.75-1.08-5.99-8.57h2.58l4.83 6.91.75 1.08 6.28 8.98h-2.58l-5.12-7.32z" fill="#fff"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderPurchaseButton() {
        return <div className='post-card-purchase-button-container'>
            <div className='post-card-purchase-button'>
                <img className='post-card-lock-img' src={lockIcon} alt="" />
                <div className='post-card-price'>{this.props.currency + ' ' + this.props.price}</div>
            </div>
        </div>
    }

    renderDelete() {
        return this.props.isDeleting ? <LoadingIndicator positionClassName='delete-packages-loading-position' contentClassName='delete-packages-loading-content' customPosition={true}/> : <img title='Delete Package' alt='Delete Package' onClick={this.handleDelete} src={deleteIcon} />;
    }

    renderPostCardCaptionBox(user, caption) {
        return <div className="post-caption-box">
            {this.renderPostCardCaption(user, caption)}
        </div>;
    }

    renderPostCardCaption(user, caption, navigateToUser) {
        return <div key={user+caption}>
            <span className="post-caption-user">{user}</span>
            <span className="post-caption">{caption}</span>
        </div>
    }

    renderPostCardComments() {
        const allComments = [
            ...(this.props.comments || []),
            ...this.state.localComments,
        ];
        return (
            <div className="card-box-comments">
                {allComments.map((c, i) => (
                    <div key={i} className="post-comment-item">
                        <span className="post-caption-user">{c.username || c.author?.publicName}</span>
                        <span className="post-caption">{c.text}</span>
                    </div>
                ))}
                {this.props.currentUser && (
                    <div className="post-comment-input-row">
                        <input
                            type="text"
                            className="post-comment-input"
                            placeholder="Add a comment..."
                            value={this.state.commentText}
                            onChange={this.handlePostCardCommentChange}
                            onKeyDown={this.handleCommentKeyDown}
                            disabled={this.state.isSubmittingComment}
                            autoFocus
                        />
                        <button
                            className="post-comment-submit"
                            onClick={this.handleCommentSubmit}
                            disabled={this.state.isSubmittingComment}
                        >
                            {this.state.isSubmittingComment ? '...' : 'Post'}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    renderPostCard() {
        return <div className="card-box posts-box">
            <div className="card-box-user">
                <a onClick={this.goToUser} name={this.props.username} href={`/@${this.props.username}`} className="card-box-user-img-link">
                    <AvatarImage size='small' identityId={this.props.owner} />
                </a>
                <div className="card-box-user-name">
                    <a onClick={this.goToUser} name={this.props.username} href={`/@${this.props.username}`}>
                        {this.props.username}
                    </a>
                </div>
            </div>
            <div className='card-box-img-container'>
                <div className={this.props.shouldBlur ? "card-box-img blur" : "card-box-img"}>
                    <img alt={this.props.username} src={this.props.image} className="full-width" />
                    {this.isPaid() ? this.renderPurchaseButton() : ''}
                </div>
            </div>
            {this.props.caption ? this.renderPostCardCaptionBox(this.props.username, this.props.caption) : ''}
            <div className="card-box-actions">
                <div className={`post-like-button${this.state.isLiked ? ' post-like-button--liked' : ''}`} onClick={this.handleLike}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={this.state.isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                    {this.state.likeCount > 0 && <span className="post-like-count">{this.state.likeCount}</span>}
                </div>
                <div className={`post-comment-button${this.state.isShowingComments ? ' post-comment-button--active' : ''}`} onClick={this.toggleComments}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    {((this.props.comments?.length || 0) + this.state.localComments.length) > 0 && (
                        <span className="post-like-count">{(this.props.comments?.length || 0) + this.state.localComments.length}</span>
                    )}
                </div>
                <div className="post-share-button" onClick={this.toggleShareSheet}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                    </svg>
                </div>
                <span className="post-date post-date--inline">{this.dateService.secondsTimestampToLocalizedDateString(this.props.date)}</span>
            </div>
            {this.renderShare()}
            {this.state.isShowingComments && this.renderPostCardComments()}
        </div>;
    }

    render() {
        return this.renderPostCard();
    }
}

export default PostCard;